import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RotateCcw, Loader2, Globe, MapPin, UserPlus } from "lucide-react";
import { motion } from "framer-motion";
import { LANGS, useLang } from "@/i18n";
import { CITIES } from "@/constants/markets";

export const Header = ({
  totalFreeAgents,
  onReset,
  resetting,
  loading,
  city,
  onCityChange,
  onOpenRegister,
}) => {
  const { t, lang, setLang } = useLang();
  const tickerItems = t("ticker");
  const tickerText = Array.isArray(tickerItems)
    ? tickerItems.join("  •  ")
    : String(tickerItems);

  return (
    <header className="bg-[hsl(var(--navy))] text-[hsl(var(--primary-foreground))]">
      {/* Breaking ticker strip */}
      <div className="bg-[hsl(var(--news-red))] text-white overflow-hidden">
        <div className="flex items-stretch">
          <span className="font-display uppercase tracking-widest text-xs sm:text-sm px-3 py-1.5 bg-[hsl(var(--news-red-dark))] shrink-0 flex items-center">
            {t("breaking")}
          </span>
          <div className="overflow-hidden flex-1 flex items-center" dir="ltr">
            <div className="ticker-track text-xs sm:text-sm font-medium py-1.5">
              <span className="px-4">{tickerText}&nbsp;&nbsp;•&nbsp;&nbsp;</span>
              <span className="px-4" aria-hidden="true">
                {tickerText}&nbsp;&nbsp;•&nbsp;&nbsp;
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Top-right actions: register + language */}
        <div className="flex flex-wrap justify-end items-center gap-2 sm:gap-3 mb-4">
          <Button
            data-testid="register-free-agent-button"
            onClick={onOpenRegister}
            className="bg-[hsl(var(--news-red))] text-white hover:bg-[hsl(var(--news-red-dark))] active:scale-[0.98] transition-colors h-9 px-4 font-semibold"
          >
            <UserPlus className="h-4 w-4 me-2" />
            {t("registerFreeAgent")}
          </Button>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger
              data-testid="language-select"
              className="w-auto gap-2 h-9 bg-[hsl(var(--navy-2))] border-white/20 text-white text-sm focus:ring-2 focus:ring-[hsl(var(--news-red))]"
            >
              <Globe className="h-4 w-4 opacity-80" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--popover))] min-w-[10rem]">
              {LANGS.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`language-option-${l.code}`}>
                  <span className="font-semibold me-2">{l.label}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{l.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          {/* Brand */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex items-center gap-1.5 bg-[hsl(var(--news-red))] text-white font-display uppercase tracking-[0.18em] text-xs px-2.5 py-1 rounded">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                {t("liveMarket")}
              </span>
            </div>
            <h1
              data-testid="app-title"
              className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none"
            >
              {t("title")}
            </h1>
            <p className="text-base md:text-lg text-white/80 mt-2">{t("subtitle")}</p>
          </div>

          {/* Market selector + Counter + Reset */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/60">
                {t("selectMarket")}
              </span>
              <Select value={city} onValueChange={onCityChange}>
                <SelectTrigger
                  data-testid="market-select"
                  className="w-[180px] gap-2 h-11 bg-white text-[hsl(var(--navy))] border-transparent font-semibold focus:ring-2 focus:ring-[hsl(var(--news-red))]"
                >
                  <MapPin className="h-4 w-4 text-[hsl(var(--news-red))] shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--popover))]">
                  <SelectItem value="all">{t("allCities")}</SelectItem>
                  {CITIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`market-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              data-testid="total-free-agents-counter"
              className="bg-white text-[hsl(var(--navy))] rounded-lg px-5 py-2.5 text-center shadow-md"
            >
              <motion.div
                key={totalFreeAgents}
                initial={{ scale: 1.12 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="font-display text-4xl leading-none tabular-nums"
              >
                {loading ? "—" : totalFreeAgents}
              </motion.div>
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[hsl(var(--muted-foreground))] mt-1">
                {t("totalFreeAgents")}
              </div>
            </div>

            <Button
              data-testid="reset-market-button"
              onClick={onReset}
              disabled={resetting || loading}
              variant="outline"
              className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[hsl(var(--navy))] active:scale-[0.98] transition-colors h-11 px-4"
            >
              {resetting ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 me-2" />
              )}
              {t("resetMarket")}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
